import { faUserCircle } from '@fortawesome/pro-regular-svg-icons/faUserCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchState, selectSearch } from '@propertypal/shared/src/reducers/search/search.slice';
import PPLogo from '@propertypal/shared/src/resources/logos/PropertyPalLogo@3x.png';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import logout from '@propertypal/shared/src/utils/logout';
import {
  ACCOUNT_LINKS,
  ROI_LINKS,
  NavLink,
  SIGN_IN_LINKS,
  STANDARD_LINKS,
  TOP_LEVEL_SALE_LINKS,
  TOP_LEVEL_RENT_LINKS,
  SEARCH_LINKS,
} from '@propertypal/web-app/src/constants/navigation';
import Link from 'next/link';
import { NextRouter, useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import ProfileAvatar from '../profile/ProfileAvatar';
import { Text } from '../typography';
import {
  ComponentBox,
  Container,
  LinkBox,
  LinksContainer,
  Logo,
  TopLevelLI,
  LinkButton,
  UserSubMenuBox,
  UserLinkBox,
  LogOutLinkButton,
  HeaderButton,
  Pill,
  HeaderIcon,
} from './Header.style';

interface Props {
  profile?: WebsiteUser;
  cfIpCountry: string | null;
}
const checkSaleType = (saleType: string, searchState?: SearchState) =>
  searchState && searchState.resultParams && searchState.resultParams.saleType?.includes(saleType);

const isActiveSearch = (link: NavLink, route: NextRouter['route'], searchState?: SearchState): boolean => {
  // ensure current page is a search page
  if (route !== '/search' && route !== '/map') return false;

  // check if link is include in sale links and whether current search is a sale search
  if (TOP_LEVEL_SALE_LINKS.includes(link.text) && checkSaleType('sale', searchState)) return true;
  // check if link is include in rental links and whether current search is a rental search
  if (TOP_LEVEL_RENT_LINKS.includes(link.text) && checkSaleType('rent', searchState)) return true;

  // you can search for either sale or rental via nlp, and the saleType in that case will be undefined
  // in the current live site, neither top level link gets highlighted, will be the same here

  return false;
};

const isActiveSearchSub = (
  link: NavLink,
  topLevelLink: NavLink,
  route: NextRouter['route'],
  searchState?: SearchState,
): boolean => {
  // ensure current page is a search page and that link has a category
  if (route !== '/search' || !link.category) return false;

  // get search category
  const category = searchState && searchState.resultParams && searchState.resultParams.category;

  // if no search category then we cannot reliably state which link is active
  if (!category) return false;

  // check if sale type is sale and top level link is a sale link
  // use the link category to compare against search category
  if (
    TOP_LEVEL_SALE_LINKS.includes(topLevelLink.text) &&
    checkSaleType('sale', searchState) &&
    category.includes(link.category)
  )
    return true;

  // check if sale type is rent  and top level link is a rental link
  // use the link category to compare against search category
  if (
    TOP_LEVEL_RENT_LINKS.includes(topLevelLink.text) &&
    checkSaleType('rent', searchState) &&
    category.includes(link.category)
  )
    return true;

  if (!checkSaleType('sale', searchState) && !checkSaleType('rent', searchState))
    if (category.includes(link.category))
      // check if neither a sale or rent search
      // this occurs in nlp search such as `homes to buy or rent in Belfast`
      // we may still want to highlight the sub nav
      return true;

  return false;
};

const isActiveUrl = (url: string, link: NavLink) => {
  let active = false;

  if (link.activeUrls) {
    link.activeUrls.forEach((activeUrl) => {
      if (url.includes(activeUrl)) active = true;
    });
  }

  if (link.sublinks) {
    link.sublinks.forEach((subLink) => {
      if (subLink.url && url.includes(subLink.url)) active = true;
    });
  }

  if (link.url && url.includes(link.url)) {
    active = true;
  }

  return active;
};

const Header: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const containerRef = useRef<HTMLElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [userMenu, setUserMenu] = useState<boolean | null>(null);
  const items = props.profile ? ACCOUNT_LINKS : SIGN_IN_LINKS;
  const links = props.cfIpCountry === 'IE' ? ROI_LINKS : STANDARD_LINKS;
  const searchState = useSelector(selectSearch);

  const togglePanel = (index: number) => {
    if (activeIndex === index) {
      setActiveIndex(null);
      setUserMenu(false);
    } else {
      setActiveIndex(index);
    }
  };

  const toggleUserPanel = () => {
    if (userMenu) {
      setUserMenu(false);
    } else {
      setUserMenu(true);
      setActiveIndex(null);
    }
  };

  useEffect(() => {
    const handleRouteChange = () => {
      // close menu on route.
      setActiveIndex(null);
      setUserMenu(false);
    };

    const handleClick = (e: MouseEvent) => {
      // @ts-ignore
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setUserMenu(false);
        setActiveIndex(null);
      }
    };

    document.addEventListener('click', handleClick);
    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const generateSubLinks = (topLevelLink: NavLink, isSearchLink: boolean) => {
    return (
      topLevelLink.sublinks &&
      topLevelLink.sublinks.map((link: NavLink) => {
        const isActive = isSearchLink
          ? isActiveSearchSub(link, topLevelLink, router.route, searchState)
          : isActiveUrl(router.asPath, link);

        return (
          <TopLevelLI key={link.text} role="none" data-sub>
            {!!link.url && (
              <LinkBox href={link.url} role="menuitem" data-testid={link.testID} prefetch={false}>
                <Text color={isActive ? theme.primary : theme.white} fontWeight="bold">
                  {link.text}
                </Text>
              </LinkBox>
            )}
          </TopLevelLI>
        );
      })
    );
  };

  const generateUserSubMenu = () => {
    return items.map((link: NavLink) => {
      return (
        <ul key={link.text} role="none" data-sub>
          {!!link.url && (
            <UserLinkBox href={link.url} prefetch={false} role="menuitem" data-testid={link.testID}>
              <Text color={router?.asPath === link.url ? theme.primary : theme.white} fontWeight="bold">
                {link.text}
              </Text>
            </UserLinkBox>
          )}

          {!link.url && (
            <LogOutLinkButton role="menuitem" onClick={() => logout()} data-testid={link.testID}>
              <Text color={theme.white} fontWeight="bold">
                {link.text}
              </Text>
            </LogOutLinkButton>
          )}
        </ul>
      );
    });
  };

  return (
    <Container
      ref={containerRef}
      isHomePage={!!router?.pathname && ['/', '/new-homes/northern-ireland'].includes(router.pathname)}
    >
      <Link aria-label="Home page" href="/" prefetch={false} style={{ display: 'flex' }}>
        <Logo src={PPLogo.src} alt="PropertyPal Logo" />
      </Link>
      <LinksContainer
        isHomePage={!!router?.pathname && ['/', '/new-homes/northern-ireland'].includes(router.pathname)}
        role="menubar"
        aria-label="Property Pal"
      >
        {links.map((link, index) => {
          const isActive = activeIndex === index;
          const isSearchLink = SEARCH_LINKS.includes(link.text);
          const activeUrl = isSearchLink
            ? isActiveSearch(link, router.route, searchState)
            : isActiveUrl(router.asPath, link);
          const isNew = link.isNew;
          const trackLink = () => {
            if (link.url === '/instant-valuation') {
              trackGaEvent('valuation_click', { link_location: 'Desktop Header', type: 'instant' });
            } else if (link.url === '/agent-valuation') {
              trackGaEvent('valuation_click', { link_location: 'Desktop Header', type: 'agent' });
            }
          };

          return (
            <TopLevelLI key={link.text} role="none" data-parent>
              {!!link.url && (
                <LinkBox href={link.url} prefetch={false} role="menuitem" data-testid={link.testID} onClick={trackLink}>
                  <Text color={activeUrl ? theme.primary : theme.white} fontWeight="bold">
                    {link.text}{' '}
                    {isNew && (
                      <>
                        &nbsp;
                        <Pill data-testid={`${link.testID}New`}>New</Pill>
                      </>
                    )}
                  </Text>
                </LinkBox>
              )}

              {link.sublinks && (
                <>
                  <LinkButton
                    role="menuitem"
                    active={isActive}
                    onClick={() => togglePanel(index)}
                    data-testid={link.testID}
                  >
                    <Text color={isActive || activeUrl ? theme.primary : theme.white} fontWeight="bold">
                      {link.text}
                    </Text>
                  </LinkButton>

                  <ComponentBox
                    data-testid={`${link.testID}-sub-box`}
                    role="menu"
                    style={{ display: isActive ? 'flex' : 'none' }}
                  >
                    {generateSubLinks(link, isSearchLink)}
                  </ComponentBox>
                </>
              )}
            </TopLevelLI>
          );
        })}
      </LinksContainer>{' '}
      {/* {navHidden && ( */}
      {/*  <Link href="/instant-valuation" passHref data-testid="instant-valuation"> */}
      {/*    <HeaderButton */}
      {/*      active={isActiveUrl(router.asPath, INSTANT_VALUATION_LINK)} */}
      {/*      onClick={trackGaLink('valuation_click', 'Mobile Header')} */}
      {/*    > */}
      {/*      <HeaderIcon> */}
      {/*        <FontAwesomeIcon */}
      {/*          color={isActiveUrl(router.asPath, INSTANT_VALUATION_LINK) ? theme.primary : theme.white} */}
      {/*          style={{ fontSize: 18 }} */}
      {/*          // TODO: Will need to update icon to faHouseBuilding when upgrade FontAwesome to v6 */}
      {/*          icon={faLaptopHouse} */}
      {/*        /> */}
      {/*      </HeaderIcon> */}
      {/*      <Text */}
      {/*        color={isActiveUrl(router.asPath, INSTANT_VALUATION_LINK) ? theme.primary : theme.white} */}
      {/*        fontSize={12} */}
      {/*        fontWeight="bold" */}
      {/*      > */}
      {/*        Instant Valuation */}
      {/*      </Text> */}
      {/*    </HeaderButton> */}
      {/*  </Link> */}
      {/* )} */}
      {!props.profile && (
        <HeaderButton active={!!userMenu} onClick={toggleUserPanel} data-testid="log-in">
          <HeaderIcon>
            <FontAwesomeIcon
              color={userMenu ? theme.primary : theme.white}
              style={{ fontSize: 18 }}
              icon={faUserCircle}
            />
          </HeaderIcon>

          <Text color={userMenu ? theme.primary : theme.white} fontSize={12} fontWeight="bold">
            Log in
          </Text>
        </HeaderButton>
      )}
      {props.profile && (
        <HeaderButton
          onClick={toggleUserPanel}
          data-testid="my-account"
          style={{ background: userMenu ? theme.textDark : 'none' }}
        >
          <HeaderIcon>
            <ProfileAvatar src={props.profile.avatarUrl} width={18} borderWidth={1} />
          </HeaderIcon>

          <Text color={userMenu ? theme.primary : theme.white} fontSize={12} fontWeight="bold">
            My Account
          </Text>
        </HeaderButton>
      )}
      <UserSubMenuBox
        data-testid="user-sub-box"
        role="menu"
        isUser={!!props.profile}
        style={{ display: userMenu ? 'flex' : 'none' }}
      >
        {generateUserSubMenu()}
      </UserSubMenuBox>
    </Container>
  );
};

export default Header;
